import './RemTemplateCreateEdit.scss';

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import Logger from '@KrashidBuilt/common/utils/logger';
import { Button, FormSection, FormContainer, InputField } from '@fifth-gear-tech/common-components/dist/components';
import { Colors, Orientation, ButtonType } from '@fifth-gear-tech/common-components/dist/utils/StyledTheme';
import { arrayMoveImmutable } from 'array-move';

import api from '../../../api';
import { ErrorMessage, Loading, SortableLabelFieldEditor } from '../../../components';
import { Flex, PreviewRemLabel } from '../../../containers';
import { FGT_REDIRECT_BASE, TEMPLATE_FIELD_TYPES } from '../../../constants';
import { ROUTE_MAP } from '../../../routes';

const logger = new Logger('RemTemplateCreateEdit');

/**
 * RemTemplateCreateEdit Component
 * @augments {Component<Props, State>}
 */
class RemTemplateCreateEdit extends Component {
    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            loading: false,
            error: null,
            selected: [],
            fields: [],
            template: {
                json_data: {
                    jobId: null,
                    fields: []
                }
            }
        };
    }

    // prune selections for qr codes that are not at the top left or right
    pruneMisplacedQrCodes = (selected) => selected.map((s, i) => {
        if (s.type == 'qr' && (i != 0 && i != 5)) {
            return { ...s, type: 'txt' };
        }
        return s;
    });

    // field label re-ordering callback
    onSortEnd = (oldIndex, newIndex) => {
        const { selected } = this.state;
        const moved = arrayMoveImmutable(selected, oldIndex, newIndex);
        this.setState({ selected: this.pruneMisplacedQrCodes(moved) });
    };

    // add label to field labels callback
    addField = (e) => this.setState({ selected: [...this.state.selected, { ...e, type: 'txt' }] })

    removeField = (selection) => {
        this.setState({ selected: this.pruneMisplacedQrCodes(this.state.selected.filter((s) => s.value != selection.value)) });
    };

    onSelectedChange = (o) => {
        logger.extra({ _: this, o });
        // default options with empty type to text
        this.setState({ selected: !o || o?.length <= 0 ? [] : o.map((v) => !v.type ? Object.assign(v, { type: TEMPLATE_FIELD_TYPES[0].value }) : v) });
    }

    onFieldTypeChange = (idx, o) => {
        logger.extra({ _: this, idx, o });
        const { selected } = this.state;
        const cloned = JSON.parse(JSON.stringify(selected));
        cloned[idx].type = o.value;
        this.onSelectedChange(cloned);
    }

    loadFields = () => {
        const { loading, template: { json_data: { jobId } } } = this.state;
        if (!loading && jobId) {
            this.setState({ loading: true, error: null }, async () => {
                try {
                    const allFields = await api.fields.getAll({ jobId });
                    const fields = [...allFields];
                    // add speedlink value to field
                    fields.push({ label: '🔗 SPEEDlink', id: 'speed-link', value: FGT_REDIRECT_BASE }); // 👈 this redirect will be replaced with part & hash data
                    // no value necessary as it is generated in the LabelField
                    this.setState({ loaded: true, loading: false, fields });
                } catch (err) {
                    logger.error(err);
                    this.setState({ loading: false, error: err });
                }
            });
        }
    }

    load = () => {
        const { template } = this.state;
        this.setState({ loading: true }, async () => {
            const res = await api.templates.getRemTemplate();
            const remTemplate = res || template;
            const selected = remTemplate.json_data.fields.map(({ type, label, id }) => ({ type, label, value: id || '' }));
            this.setState((prevState) => ({ ...prevState, loading: false, template: remTemplate, selected }), this.loadFields);
        });
    }

    componentDidMount() {
        const { loaded } = this.state;
        if (!loaded) {
            this.load();
        }
    }

    onSave = async () => {
        const { fields, selected, template: { json_data } } = this.state;

        const payload = { json_data };

        payload.json_data.fields = selected.map((s) => {
            const found = fields.find((f) => f.id === s.value);
            if (found) {
                return Object.assign(found, { type: s.type || TEMPLATE_FIELD_TYPES[0].value });
            }
        }).filter((f) => f); // remove undefined

        await api.templates.createRemTemplate(payload);

        this.props.history.push(ROUTE_MAP.SETTINGS.TEMPLATES);
    }

    setTemplateField = (key, value) => {
        const { template } = this.state;
        const cloned = JSON.parse(JSON.stringify(template));
        cloned[key] = value;
        this.setState({ template: cloned });
    }

    setTemplateJsonField = (key, value) => {
        const { template } = this.state;
        const cloned = JSON.parse(JSON.stringify(template));
        cloned['json_data'][key] = value;
        this.setState({ template: cloned });
    }

    updateJobId = (e) => {
        this.setState((prevState) => ({ ...prevState, fields: [] }));
        this.setTemplateJsonField('jobId', e.target.value);
    }

    isValid = () => {
        const { selected } = this.state;
        return selected?.length > 0;
    }

    render() {
        logger.extra({ _: this });
        const { error, loading, fields, selected, template: { json_data: { jobId } } } = this.state;

        if (loading) {
            return (<Loading />);
        }

        const options = fields.map((f) => ({
            value: f.id,
            // append the moraware form template name if it exists- else just use normal label
            label: `${f?.mwFormTemplateName ? `${f.label} (${f.mwFormTemplateName})` : f.label}`,
        }));

        const filtered = [];

        for (let i = 0; i < selected?.length; i++) {
            const found = fields.find((f) => f.id === selected[i].value);
            if (found) {
                filtered.push(Object.assign(found, { type: selected[i].type }));
            }
        }

        return (
            <div className={'RemTemplateCreateEdit'}>
                <Flex>
                    <FormContainer title={''} onSubmit={(e) => {
                        e.preventDefault(); this.loadFields();
                    }}
                    buttons={
                        [
                            <input key={'holder'} style={{ display: 'none' }} type='submit' />,
                            <Button key={'save'} className={'SaveButton'} colorTheme={Colors.Orange} label={'Save'} type={ButtonType.Save} onClick={this.onSave} disabled={!this.isValid()} />
                        ]
                    }
                    >
                        <FormSection label={'Remnant Template'} subtext={'Setup your Remnant Template'} colorTheme={Colors.Grey} orientation={Orientation.Mobile}>

                            <InputField
                                label={'Job ID'}
                                defaultValue={jobId || null}
                                onSubmit={this.loadFields}
                                onBlur={this.loadFields}
                                onChange={(e) => {
                                    this.updateJobId(e);
                                }}
                                clearIcon={true}
                                onIconClick={() => this.updateJobId({ target: { value: '' } })}
                            />

                            {error && (
                                <ErrorMessage error={error} />
                            )}

                        </FormSection>
                    </FormContainer>

                    {(jobId && fields.length > 0) &&
                        <div className={'preview'}>

                            <PreviewRemLabel fields={filtered} />

                            <SortableLabelFieldEditor
                                selected={selected}
                                options={options}
                                onSortEnd={this.onSortEnd}
                                addField={this.addField}
                                removeField={this.removeField}
                                onFieldTypeChange={this.onFieldTypeChange}
                            />

                        </div>
                    }

                </Flex>

            </div >
        );
    }
}

RemTemplateCreateEdit.defaultProps = {
};

RemTemplateCreateEdit.propTypes = {
    match: PropTypes.shape(),
    history: PropTypes.shape(),
};


export default RemTemplateCreateEdit;